import React from 'react';
import { Routes, Route } from 'react-router-dom';
import APIService from '../../services/APIService';
import { isLoggedIn, currentUserToken, storeSession, clearSession } from '../../common/auth';
import { silentSignIn, isSSOSessionEnabled } from '../../common/ssoSession';
import Layout from '../layout/Layout';
import Home from '../../pages/home/Home';
import AboutUs from '../../pages/about/AboutUs';
import Tools from '../../pages/tools/Tools';
import Pricing from '../../pages/pricing/Pricing';
import ToolDetail from '../../pages/tools/ToolDetail';
import Blog from '../../pages/blog/Blog';
import BlogPost from '../../pages/blog/BlogPost';
import ContentPage from '../../pages/content/ContentPage';
import EarlyAccess from '../../pages/early-access/EarlyAccess';
import OIDLoginCallback from '../auth/OIDLoginCallback';
import NotFound from '../../pages/errors/NotFound';
import Unauthorized from '../../pages/errors/Unauthorized';

const CALLBACK_PATH = /^\/oidc\/login(\/|$)/i;

const exchangeCode = body => APIService.post('/users/oidc/code-exchange/', body).then(response => response.data);

const useSilentSignIn = onSignedIn => {
  const running = React.useRef(false);

  React.useEffect(() => {
    const trySilentSignIn = () => {
      if (running.current || isLoggedIn() || !isSSOSessionEnabled() || CALLBACK_PATH.test(window.location.pathname))
        return;
      running.current = true;
      silentSignIn(exchangeCode).then(tokens => {
        if (!tokens) {
          if (currentUserToken())
            clearSession();
          return null;
        }
        if (isLoggedIn())
          return null;
        storeSession(tokens.access_token, tokens.id_token);
        return APIService.get('/user/', { includeSubscribedOrgs: true, includeFollowing: true }).then(profile => {
          if (profile.status === 200)
            localStorage.setItem('user', JSON.stringify(profile.data));
          onSignedIn();
        });
      }).then(() => { running.current = false; }, () => { running.current = false; });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible')
        trySilentSignIn();
    };
    trySilentSignIn();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
};

const App = () => {
  const [authVersion, setAuthVersion] = React.useState(0);
  useSilentSignIn(() => setAuthVersion(version => version + 1));
  return (
  <React.Fragment key={authVersion}>
  <Routes>
    <Route element={<Layout />}>
      <Route path="/" element={<Home />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/tools/:slug" element={<ToolDetail />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* /guides and /guides/:slug are offline until the section is built
          out (ocl_online#215, ocl_online#216). src/pages/guides and the
          content pipeline are kept; restore the routes here and in
          scripts/lib/routes.js. */}
      <Route path="/faq" element={<ContentPage titleKey="footer.faq" file="faq.md" faqSchema />} />
      <Route path="/privacy-policy" element={<ContentPage titleKey="footer.privacy" file="privacy-policy.md" />} />
      <Route path="/license" element={<ContentPage titleKey="footer.license" file="license.md" />} />
      <Route path="/code-of-conduct" element={<ContentPage titleKey="footer.code_of_conduct" file="ocl-code-of-conduct.md" />} />
      <Route path="/terms-of-use" element={<ContentPage titleKey="footer.terms" file="terms-of-use.md" />} />
      <Route path="/newsletter/check-inbox" element={<ContentPage titleKey="newsletter.check_inbox" file="newsletter-check-inbox.md" robots="noindex, follow" />} />
      <Route path="/newsletter/confirmed" element={<ContentPage titleKey="newsletter.confirmed" file="newsletter-confirmed.md" robots="noindex, follow" />} />
      <Route path="/early-access" element={<EarlyAccess />} />
      <Route path="/pro-early-access-agreement" element={<ContentPage titleKey="agreements.pro_early_access" file="pro-early-access-agreement.md" robots="noindex, follow" />} />
      <Route path="/oidc/login" element={<OIDLoginCallback />} />
      <Route path="/401" element={<Unauthorized />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
  </React.Fragment>
  );
};

export default App;
